import apiClient from './client';
import { keysToCamelCase } from '@/lib/api-utils';
import type { AvailableOrgMember } from '@/types/project';
import type {
  CreateEnterprisePositionDto,
  EnterprisePosition,
  LeaveOrgResult,
  MemberIdentity,
  MemberRealName,
  OrgMemberProjectTreeResponse,
  MemberProjectResidue,
  MemberListResponse,
  OrgMember,
  UpdateEnterprisePositionDto,
  UpdateMemberIdentityDto,
  UpdateMemberRealNameDto,
  UpdateMemberRoleDto,
  UpdateReportAccessScopeDto,
} from '@/types/org';

export const orgMembersApi = {
  /** 获取成员列表 */
  async listMembers(
    orgId: string,
    params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      orgRole?: number;
      roleTemplateCode?: string;
      customGroupId?: string;
      positionId?: string;
      memberStatus?: number;
    }
  ): Promise<MemberListResponse> {
    const response = await apiClient.get(`/orgs/${orgId}/members`, {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        search: params?.search,
        orgRole: params?.orgRole,
        roleTemplateCode: params?.roleTemplateCode,
        customGroupId: params?.customGroupId,
        positionId: params?.positionId,
        memberStatus: params?.memberStatus,
      },
    });
    return keysToCamelCase<MemberListResponse>(response);
  },

  /** 修改成员角色 */
  async updateMemberRole(
    orgId: string,
    targetUserId: string,
    dto: UpdateMemberRoleDto
  ): Promise<OrgMember> {
    return apiClient.put<OrgMember>(`/orgs/${orgId}/members/${targetUserId}/role`, {
      role: dto.role,
    }) as unknown as Promise<OrgMember>;
  },

  async updateMemberIdentity(
    orgId: string,
    targetUserId: string,
    dto: UpdateMemberIdentityDto
  ): Promise<MemberIdentity> {
    const response = await apiClient.patch(`/orgs/${orgId}/members/${targetUserId}/identity`, dto);
    return keysToCamelCase<MemberIdentity>(response);
  },

  async updateMemberRealName(
    orgId: string,
    targetUserId: string,
    dto: UpdateMemberRealNameDto
  ): Promise<MemberRealName> {
    const response = await apiClient.patch(`/orgs/${orgId}/members/${targetUserId}/real-name`, dto);
    return keysToCamelCase<MemberRealName>(response);
  },

  async updateReportAccessScope(
    orgId: string,
    targetUserId: string,
    dto: UpdateReportAccessScopeDto
  ): Promise<OrgMember> {
    const response = await apiClient.patch(
      `/orgs/${orgId}/members/${targetUserId}/report-access-scope`,
      dto
    );
    return keysToCamelCase<OrgMember>(response);
  },

  async getMemberProjectResidue(
    orgId: string,
    targetUserId: string
  ): Promise<MemberProjectResidue> {
    const response = await apiClient.get(`/orgs/${orgId}/members/${targetUserId}/project-residue`);
    return keysToCamelCase<MemberProjectResidue>(response);
  },

  async listMemberProjects(
    orgId: string,
    targetUserId: string
  ): Promise<OrgMemberProjectTreeResponse> {
    const response = await apiClient.get(`/orgs/${orgId}/members/${targetUserId}/projects`);
    return keysToCamelCase<OrgMemberProjectTreeResponse>(response);
  },

  async updateProducerCreditLimit(
    orgId: string,
    targetUserId: string,
    producerCreditLimit: number
  ): Promise<OrgMember> {
    const response = await apiClient.patch(
      `/orgs/${orgId}/members/${targetUserId}/producer-credit-limit`,
      {
        producerCreditLimit,
      }
    );
    return keysToCamelCase<OrgMember>(response);
  },

  async listPositions(orgId: string): Promise<EnterprisePosition[]> {
    const response = await apiClient.get(`/orgs/${orgId}/positions`);
    return keysToCamelCase<EnterprisePosition[]>(response);
  },

  async createPosition(
    orgId: string,
    dto: CreateEnterprisePositionDto
  ): Promise<EnterprisePosition> {
    const response = await apiClient.post(`/orgs/${orgId}/positions`, dto);
    return keysToCamelCase<EnterprisePosition>(response);
  },

  async updatePosition(
    orgId: string,
    positionId: string,
    dto: UpdateEnterprisePositionDto
  ): Promise<EnterprisePosition> {
    const response = await apiClient.put(`/orgs/${orgId}/positions/${positionId}`, dto);
    return keysToCamelCase<EnterprisePosition>(response);
  },

  async deletePosition(orgId: string, positionId: string): Promise<void> {
    await apiClient.delete(`/orgs/${orgId}/positions/${positionId}`);
  },

  async updateMemberPositions(
    orgId: string,
    targetUserId: string,
    positionIds: string[]
  ): Promise<{ userId: string; positionIds: string[] }> {
    const response = await apiClient.put(`/orgs/${orgId}/members/${targetUserId}/positions`, {
      positionIds,
    });
    return keysToCamelCase<{ userId: string; positionIds: string[] }>(response);
  },

  /** 暂停成员 */
  async suspendMember(orgId: string, targetUserId: string): Promise<void> {
    await apiClient.post(`/orgs/${orgId}/members/${targetUserId}/suspend`);
  },

  /** 恢复成员 */
  async activateMember(orgId: string, targetUserId: string): Promise<void> {
    await apiClient.post(`/orgs/${orgId}/members/${targetUserId}/activate`);
  },

  /** 移除成员 */
  async removeMember(orgId: string, targetUserId: string): Promise<void> {
    await apiClient.delete(`/orgs/${orgId}/members/${targetUserId}`);
  },

  /** 退出组织（自助） */
  async leaveOrg(orgId: string): Promise<LeaveOrgResult> {
    const response = await apiClient.post(`/orgs/${orgId}/members/leave`);
    return keysToCamelCase<LeaveOrgResult>(response);
  },

  /**
   * RFC-003 §6.1：获取组织成员池，用于项目邀请弹窗。
   * GET /api/v1/orgs/:orgId/members/available-for-project?projectId=...
   *
   * 返回每个活跃组织成员 + `alreadyInProject` 标记。
   */
  async listAvailableForProject(
    orgId: string,
    projectId: string,
    positionIds?: string[]
  ): Promise<AvailableOrgMember[]> {
    const response = await apiClient.get(`/orgs/${orgId}/members/available-for-project`, {
      params: { projectId, positionIds },
    });
    return keysToCamelCase<AvailableOrgMember[]>(response);
  },
};
